/**
 * Wish Detail Modal Component
 * Desktop modal for viewing wish details with navigation
 * Design Reference: docs/home-page-redesign.md (Luxury Gold Theme)
 */

import { useState, useEffect } from 'react';
import api from '../../../services/api';
import type { ApiWish } from '../../../types';
import { useAuth } from '../../../contexts/AuthContext';
import './WishDetailModal.css';

interface WishDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  wishId: string | null;
  allWishIds?: string[];
  onWishChange?: (wishId: string) => void;
}

const WishDetailModal = ({
  isOpen,
  onClose,
  wishId,
  allWishIds = [],
  onWishChange,
}: WishDetailModalProps) => {
  const { isAuthenticated, user } = useAuth();
  const [wish, setWish] = useState<ApiWish | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contacting, setContacting] = useState(false);
  const [contactMessage, setContactMessage] = useState('');

  // Calculate current index and prev/next availability
  const currentIndex = allWishIds.indexOf(wishId || '');
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < allWishIds.length - 1;
  const prevWishId = hasPrev ? allWishIds[currentIndex - 1] : null;
  const nextWishId = hasNext ? allWishIds[currentIndex + 1] : null;

  useEffect(() => {
    if (!isOpen || !wishId) {
      setWish(null);
      setError(null);
      return;
    }

    const loadWish = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get<ApiWish>(`/api/wishes/${wishId}`);
        if (response.success && response.data) {
          setWish(response.data);
        } else {
          setError('找不到此許願');
        }
      } catch (err) {
        console.error('Error loading wish:', err);
        setError('載入許願時發生錯誤');
      } finally {
        setLoading(false);
      }
    };

    loadWish();
  }, [isOpen, wishId]);

  const handleContact = async () => {
    if (!wishId || !isAuthenticated) {
      // Redirect to login - Modal will close and user will be navigated
      window.location.href = '/login';
      return;
    }

    // Check if user has permission to contact (free plan users cannot contact)
    const userPlan = user?.plan || 'free';
    if (userPlan === 'free') {
      setContactMessage('聯絡許願者需要付費方案。將導向至方案頁面...');
      setTimeout(() => {
        window.location.href = '/pricing';
      }, 2000);
      return;
    }

    setContacting(true);
    setContactMessage('');

    try {
      const response = await api.post<{ success: boolean }>(`/api/wishes/${wishId}/contact`, {});
      if (response.success) {
        setContactMessage('聯繫請求已發送！許願者將會收到您的聯繫資訊。');
      } else {
        setContactMessage(response.error?.message || '聯繫失敗，請稍後再試。');
      }
    } catch {
      setContactMessage('聯繫失敗，請稍後再試。');
    } finally {
      setContacting(false);
    }
  };

  const handlePrevious = () => {
    if (prevWishId && onWishChange) {
      setError(null);
      setContactMessage('');
      onWishChange(prevWishId);
    }
  };

  const handleNext = () => {
    if (nextWishId && onWishChange) {
      setError(null);
      setContactMessage('');
      onWishChange(nextWishId);
    }
  };

  const handleClose = () => {
    setContactMessage('');
    onClose();
  };

  if (!isOpen) return null;

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      '維修': '汽車維修',
      '保養': '汽車保養',
      '美容': '汽車美容',
      '改裝': '汽車改裝',
    };
    return labels[category] || category;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'open': '尋找中',
      'matched': '配對中',
      'closed': '已結案',
    };
    return labels[status] || status;
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '未知';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '未知';
    if (date.getFullYear() === 1970) return '未知';
    return date.toLocaleString('zh-TW');
  };

  return (
    <div className="wish-detail-modal-overlay" onClick={handleClose}>
      <div className="wish-detail-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Navigation Bar */}
        <div className="wish-detail-modal__nav">
          <button
            className="wish-detail-modal__nav-btn"
            onClick={handlePrevious}
            disabled={!hasPrev}
            aria-label="上一則"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            <span>上一則</span>
          </button>
          <span className="wish-detail-modal__nav-counter">
            {currentIndex + 1} / {allWishIds.length || 1}
          </span>
          <button
            className="wish-detail-modal__nav-btn"
            onClick={handleNext}
            disabled={!hasNext}
            aria-label="下一則"
          >
            <span>下一則</span>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="wish-detail-modal__loading">載入中...</div>
        ) : error || !wish ? (
          <div className="wish-detail-modal__error">
            <p>{error || '找不到此許願'}</p>
          </div>
        ) : (
          <div className="wish-detail-modal__content">
            {/* Info Section */}
            <div className="wish-detail-modal__info">
              <h1 className="wish-detail-modal__title">{wish.title}</h1>

              <div className="wish-detail-modal__meta">
                <span className={`wish-detail-modal__status wish-detail-modal__status--${wish.status}`}>
                  {getStatusLabel(wish.status)}
                </span>
                {wish.urgent && (
                  <span className="wish-detail-modal__urgent">🔥 急件</span>
                )}
              </div>

              <div className="wish-detail-modal__tags">
                <span className="tag tag--service">🔧 {getCategoryLabel(wish.category)}</span>
                <span className="tag tag--region">📍 {wish.region}</span>
                {wish.district && (
                  <span className="tag tag--region">📍 {wish.district}</span>
                )}
                {wish.budget && (
                  <span className="tag tag--budget">💰 NT{wish.budget}</span>
                )}
              </div>

              {wish.description && (
                <div className="wish-detail-modal__section">
                  <h3>需求描述</h3>
                  <p>{wish.description}</p>
                </div>
              )}

              <div className="wish-detail-modal__section">
                <h3>詳細資訊</h3>
                <div className="wish-detail-modal__info-grid">
                  <div className="wish-detail-modal__info-item">
                    <span className="wish-detail-modal__label">發布者：</span>
                    <span>{wish.userName || '匿名'}</span>
                  </div>
                  <div className="wish-detail-modal__info-item">
                    <span className="wish-detail-modal__label">發布時間：</span>
                    <span>{formatDate(wish.createdAt)}</span>
                  </div>
                  <div className="wish-detail-modal__info-item">
                    <span className="wish-detail-modal__label">瀏覽次數：</span>
                    <span>{wish.viewCount}</span>
                  </div>
                </div>
              </div>

              {/* Contact Section */}
              {wish.status === 'open' && (
                <div className="wish-detail-modal__actions-section">
                  {contactMessage && (
                    <div className={`wish-detail-modal__message ${
                      contactMessage.includes('成功') || contactMessage.includes('發送') ? 'success' : 'error'
                    }`}>
                      {contactMessage}
                    </div>
                  )}
                  {isAuthenticated ? (
                    <button
                      onClick={handleContact}
                      disabled={contacting}
                      className="btn btn--primary wish-detail-modal__contact-btn"
                    >
                      {contacting ? '發送中...' : '📞 聯繫許願者'}
                    </button>
                  ) : (
                    <div className="wish-detail-modal__login-prompt">
                      <p>請先登入以聯繫許願者</p>
                      <button
                        onClick={() => window.location.href = '/login'}
                        className="btn btn--outline"
                      >
                        前往登入
                      </button>
                    </div>
                  )}
                </div>
              )}

              {wish.status !== 'open' && (
                <div className="wish-detail-modal__closed-notice">
                  此許願已結案或配對中，無法再聯繫
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WishDetailModal;
